// ============================================================
// BANKE U SRBIJI — šifra banke (prve 3 cifre računa) → SWIFT, i IBAN iz domaćeg računa.
// SWIFT kodovi: zvanični SwiftRef spisak (glavni BIC banke); šifre potvrdio administrator.
// ============================================================
import { supabase } from './supabase';

export const BANKS = {
  '105': { name: 'AIK Banka', swift: 'AIKBRS22' },
  '115': { name: 'Yettel Bank', swift: 'AAAARSBG' },
  '145': { name: 'Adriatic Bank', swift: 'LIKIRSBG' },
  '155': { name: 'Halkbank', swift: 'CABARS22' },
  '160': { name: 'Banca Intesa', swift: 'DBDBRSBG' },
  '165': { name: 'Addiko Bank', swift: 'HAABRSBG' },
  '170': { name: 'UniCredit Bank', swift: 'BACXRSBG' },
  '200': { name: 'Banka Poštanska štedionica', swift: 'SBPORSBG' },
  '205': { name: 'NLB Komercijalna banka', swift: 'KOBBRSBG' },
  '220': { name: 'ProCredit Bank', swift: 'PRCBRSBG' },
  '265': { name: 'Raiffeisen Bank', swift: 'RZBSRSBG' },
  '295': { name: 'Srpska banka', swift: 'SRBNRSBG' },
  '325': { name: 'OTP banka', swift: 'OTPVRS22' },
  '340': { name: 'Erste Bank', swift: 'GIBARS22' },
  '370': { name: '3 Banka', swift: 'OPPBRS22' },
  '375': { name: 'API Bank', swift: 'APIBRSBG' },
  '380': { name: 'Mirabank', swift: 'MRBNRSBG' },
  '385': { name: 'Bank of China Srbija', swift: 'BKCHRSBG' },
};

// Šifarnici registra (preuzeti 2026-11-05): status i vrsta računa
const STATUS_LABELS = {
  '0': 'aktivan', '100': 'aktivan', '1': 'blokirana odobrenja', '2': 'blokirana zaduženja',
  '3': 'blokiran (prinudna naplata)', '4': 'ugašen', '104': 'ugašen', '8': 'privremeno neaktivan',
};
export const isActiveAccount = (a) => !a.status || a.status === '0' || a.status === '100';
export const isFxAccount = (a) => a.type === '2';
export const accountStatusLabel = (a) => STATUS_LABELS[a.status] || '';
export const accountTypeLabel = (a) => (a.type === '2' ? 'devizni' : a.type === '1' ? 'dinarski' : '');

// Predlog: prvi aktivan dinarski račun, a za inostranstvo samo pravi devizni račun iz registra.
// Ako ga registar ne vrati, IBAN se ne pogađa (kod nekih banaka devizni račun ima drugi broj).
export function suggestAccounts(accounts) {
  const list = accounts || [];
  const active = list.filter(isActiveAccount);
  const dom = active.find((a) => !isFxAccount(a)) || list.find((a) => !isFxAccount(a)) || list[0] || null;
  const fx = active.find(isFxAccount) || null;
  return { dom, fx };
}

// Domaći račun "265-0000001234567-89" → 18 cifara (3 + 13 + 2), ili '' ako nije ispravan
export function accountDigits(account) {
  const parts = String(account || '').trim().split('-');
  if (parts.length === 3) {
    const [b, n, c] = parts.map((p) => p.replace(/\D/g, ''));
    if (b && n && c) return b.padStart(3, '0') + n.padStart(13, '0') + c.padStart(2, '0');
  }
  const d = String(account || '').replace(/\D/g, '');
  return d.length === 18 ? d : '';
}

// Srpski IBAN = "RS35" + 18 cifara računa (kontrolni broj je za srpske račune uvek 35); u grupama po 4
export function ibanFromAccount(account) {
  const d = accountDigits(account);
  if (!d) return '';
  return `RS35${d}`.replace(/(.{4})/g, '$1 ').trim();
}

export const bankOf = (account) => BANKS[accountDigits(account).slice(0, 3)] || null;

// Isti račun bez obzira na zapis (sa crticama, IBAN, razmaci)
export function sameAccount(a, b) {
  const x = accountDigits(a) || String(a || '').replace(/\s/g, '').replace(/^RS35/i, '');
  const y = accountDigits(b) || String(b || '').replace(/\s/g, '').replace(/^RS35/i, '');
  return Boolean(x) && x === y;
}

// Registar uz mesto ponekad upiše broj pošte ("BEOGRAD 150") ili opštinu ("BEOGRAD (STARI GRAD)") — ostavi samo mesto
export const cleanCity = (city) => String(city || '').replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+\d+$/, '').trim();

// Pretraga firme po PIB-u (Supabase funkcija); vraća { company, accounts } ili baca grešku sa porukom
export async function lookupCompany(pib) {
  const { data, error } = await supabase.functions.invoke('nbs-company-lookup', { body: { pib } });
  let problem = '';
  if (error) {
    problem = error.message;
    try { const b = await error.context?.json(); if (b?.error) problem = b.error; } catch { /* ignore */ }
  } else if (data?.error) problem = data.error;
  if (problem) throw new Error('Podaci trenutno nisu mogli da se preuzmu. Pokušajte ponovo ili ih unesite ručno.');
  if (!data?.found) throw new Error('Za ovaj PIB nema podataka. Proverite broj ili unesite podatke ručno.');
  const company = { ...(data.company || {}) };
  if (company.city) company.city = cleanCity(company.city);
  return { company, accounts: data.accounts || [] };
}

// Adresa iz registra "KONDINA 26", "KOSOVSKA 51 /6", "NEMANJINA 4-A", "GLAVNA BB" → ulica i broj
export function splitAddress(address) {
  const addr = String(address || '').trim().replace(/\s+/g, ' ');
  const m = addr.match(/^(.*?)[\s,]+((?:\d+\w*|BB)(?:\s*[/-]\s*\w+)*)$/i);
  return { street: m ? m[1] : addr, street_number: m ? m[2].replace(/\s*([/-])\s*/g, '$1') : '' };
}

// Ime preduzetnika iz naziva "Ime Prezime PR ..."
export const ownerFromName = (name) => (/\sPR\s/i.test(name || '') ? String(name).split(/\sPR\s/i)[0].trim() : '');

// Podaci za devizni račun izabranog računa: IBAN, SWIFT i naziv banke
export function foreignFields(acc) {
  const b = bankOf(acc.account);
  return { account_ino: ibanFromAccount(acc.account), swift: b?.swift || '', iban_bank: acc.bank || b?.name || '' };
}
